import { Id, Loading, NotFound, isSuccess } from "@dgs/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Form, Formik, FormikErrors } from "formik";
import { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Route, Routes, useNavigate, useParams } from "react-router";
import styled from "styled-components";
import { eventService as sharedEventService } from "../../api/eventService";
import { EventCostCategoryResource, EventOffboardingFormValues } from "../../types/event";
import { EventOffboardingCosts } from "./EventOffboardingCosts";
import { EventOffboardingKeyMetrics } from "./EventOffboardingKeyMetrics";
import { EventOffboardingStepper } from "./EventOffboardingStepper";
import { EventOffboardingThankYouPage } from "./EventOffboardingThankYouPage";

const EventOffboardingGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	gap: ${({ theme }) => theme.spacing(4)};

	${({ theme }) => theme.breakpoints.desktop} {
		grid-template-columns: 1fr 3fr;
	}
`;

interface Props {
	variant: "outlined" | "elevated";
}

export const EventOffboardingPage: FC<Props> = ({ variant }) => {
	const { t } = useTranslation();
	const { eventId = "" } = useParams();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { data: event, isLoading } = useQuery({
		queryKey: sharedEventService.detailsKey(eventId),
		queryFn: () => sharedEventService.getDetails(eventId).then((x) => x.data.data),
	});

	const validateOffboarding = useCallback(
		(formValues: EventOffboardingFormValues) => {
			const errors: FormikErrors<EventOffboardingFormValues> = {};
			if (event?.eventType.visitorsMetricRequirement === "required" && formValues.visitorsMetric === null) {
				errors.visitorsMetric = t("Required field");
			}
			if (event?.eventType.invitedPersonsMetricRequirement === "required" && formValues.invitedPersonsMetric === null) {
				errors.invitedPersonsMetric = t("Required field");
			}
			if (event?.eventType.registrationsMetricRequirement === "required" && formValues.registrationsMetric === null) {
				errors.registrationsMetric = t("Required field");
			}
			if (event?.eventType.customersMetricRequirement === "required" && formValues.customersMetric === null) {
				errors.customersMetric = t("Required field");
			}
			if (event?.eventType.employeesMetricRequirement === "required" && formValues.employeesMetric === null) {
				errors.employeesMetric = t("Required field");
			}
			if (event?.eventType.eventReachMetricRequirement === "required" && formValues.eventReachMetric === null) {
				errors.eventReachMetric = t("Required field");
			}
			if (
				event?.eventType.eventGoalRatingMetricRequirement === "required" &&
				formValues.eventGoalRatingMetric === null
			) {
				errors.eventGoalRatingMetric = t("Required field");
			}

			const costsErrors: { [K in keyof EventCostCategoryResource]?: any }[] = [];

			formValues.costCategories.forEach((value, index) => {
				costsErrors[index] = {};
				if (
					event?.eventType.costCategoryRequirements.find((r) => value.costCategory.id === r.costCategory.id)
						?.requirement === "required" &&
					value?.actualCost === null
				) {
					costsErrors[index].actualCost = t("Field is required");
				}
			});

			if (costsErrors.some((e) => Object.keys(e).length > 0)) {
				errors.costCategories = costsErrors;
			}

			return errors;
		},
		[event?.eventType, t],
	);

	const finishOffboarding = useCallback(
		async (formValues: EventOffboardingFormValues) => {
			const res = await sharedEventService.offboardEvent(eventId, {
				...formValues,
				eventTypeId: event?.eventType.id as Id,
				costCategories: formValues.costCategories.map((costCategory) => ({
					costCategoryId: costCategory.costCategory.id,
					plannedCost: costCategory.plannedCost,
					actualCost: costCategory.actualCost,
					comment: costCategory.comment,
				})),
			});

			if (isSuccess(res)) {
				queryClient.invalidateQueries({ queryKey: sharedEventService.detailsKey(eventId) });
				queryClient.invalidateQueries({ queryKey: sharedEventService.showKey(eventId) });
				navigate("../thank-you");
			}
		},
		[event?.eventType.id, eventId, navigate, queryClient],
	);

	const initialValues: EventOffboardingFormValues = useMemo(
		() => ({
			customersMetric: event?.customersMetric || null,
			employeesMetric: event?.employeesMetric || null,
			eventGoalRatingMetric: event?.eventGoalRatingMetric || null,
			eventReachMetric: event?.eventReachMetric || null,
			invitedPersonsMetric: event?.invitedPersonsMetric || null,
			registrationsMetric: event?.registrationsMetric || null,
			visitorsMetric: event?.visitorsMetric || null,
			costCategories: event?.costCategories || [],
		}),
		[
			event?.costCategories,
			event?.customersMetric,
			event?.employeesMetric,
			event?.eventGoalRatingMetric,
			event?.eventReachMetric,
			event?.invitedPersonsMetric,
			event?.registrationsMetric,
			event?.visitorsMetric,
		],
	);

	if (!event || isLoading) {
		return <Loading />;
	}

	return (
		<Formik
			initialValues={initialValues}
			onSubmit={finishOffboarding}
			validate={validateOffboarding}
			validateOnMount={true}
		>
			<Form>
				<EventOffboardingGrid>
					<EventOffboardingStepper event={event} />
					<Routes>
						<Route path="key-metrics" element={<EventOffboardingKeyMetrics event={event} variant={variant} />} />
						<Route path="costs" element={<EventOffboardingCosts event={event} variant={variant} />} />
						<Route path="thank-you" element={<EventOffboardingThankYouPage />} />
						<Route path="*" element={<NotFound />} />
					</Routes>
				</EventOffboardingGrid>
			</Form>
		</Formik>
	);
};
